import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { handleInPageHashLinkClick, handleLandingLogoHomeClick } from '~/landing_page/lib/inPageNav';
import { cn } from '~/lib/utils';
import styles from './HeaderNavV2.module.css';
import { useIsomorphicLayoutEffect } from '~/lib/useIsomorphicLayoutEffect';

/**
 * Landing v2 header: the light landing's `HeaderNav` geometry on the dark ground, plus x.ai's menus,
 * measured off x.ai's own DOM (2026-09-04) rather than a screenshot:
 *
 * - ONE card, not one per menu. It hangs 8px under the bar, left-aligned to whichever trigger is
 *   open, and when the pointer moves to the next trigger the SAME card slides and resizes to the
 *   new content while the old and new contents crossfade with a small horizontal drift.
 * - The card is #1a1a1a (surface-3 here), 12px corners, a soft black shadow, and clips its content.
 * - A menu is a 220px list, 8px padding, each item 12x16 padded on an 8px radius: a 14px/500 white
 *   title over an 11px/400 description at 40% white. A menu with a preview adds a right column
 *   holding a raised well; a menu without one is just the list, narrow, with a quiet "explore all"
 *   row at the foot.
 * - The trigger is 14px/500 at 54% white with a 12px chevron; open, both go white and the chevron
 *   turns up.
 *
 * Opens on hover or click; closes on leaving, Esc, or a click anywhere else.
 */

interface MenuItem {
  title: string;
  description: string;
  href: string;
}

interface Menu {
  key: string;
  label: string;
  items: readonly MenuItem[];
  /** The right column, when the menu has one. */
  preview?: boolean;
  /** The quiet row at the foot: "Explore all use cases ↗". */
  more?: { label: string; href: string };
}

const MENUS: readonly Menu[] = [
  {
    key: 'product',
    label: 'Product',
    preview: true,
    items: [
      { title: 'Agent', description: 'Asks one question before it builds anything.', href: '/#features' },
      { title: 'Context', description: 'Pulls Figma, Linear and Notion into the brief.', href: '/#features' },
      { title: 'Present', description: 'A complete canvas you can edit and present.', href: '/#features' },
      { title: 'Brand kits', description: 'Reads your site into a kit and applies it.', href: '/#features' },
    ],
  },
  {
    key: 'who',
    label: "Who it's for",
    items: [
      { title: 'Founders', description: 'Pitching the next round.', href: '/#use-cases' },
      { title: 'Product managers', description: 'Aligning the team on what is next.', href: '/#use-cases' },
      { title: 'Designers', description: 'Making their case.', href: '/#use-cases' },
      { title: 'Engineers', description: 'Explaining the hard stuff.', href: '/#use-cases' },
      { title: 'Consultants', description: 'On a client deadline.', href: '/#use-cases' },
    ],
    more: { label: 'See who uses Deck', href: '/#use-cases' },
  },
];

/**
 * What the bar actually shows, left to right.
 *
 * `menu` names an entry in `MENUS` and makes the item a dropdown trigger; without one it is a plain
 * in-page link. Both menus are parked for now (Beka, 2026-09-12) — the card, its slide and the
 * crossfade stay wired below, so bringing one back is a `menu` key on the item that wants it.
 */
const NAV_LINKS: readonly { label: string; href: string; menu?: string }[] = [
  { label: 'How it works', href: '/#features' },
  { label: 'Features', href: '/#workspace' },
  { label: 'Pricing', href: '/#pricing' },
];

/** The well in the Product menu: x.ai shows a chat there; ours is a Deck exchange. */
const CHAT: readonly { who: 'you' | 'agent'; text: string }[] = [
  { who: 'agent', text: 'One thing first. Open on the retention number, or on what you built?' },
  { who: 'you', text: 'Retention. Use the look from the report.' },
  { who: 'agent', text: 'Ok. Retention opens it, the workspace is the middle, the ask is last. Eight artifacts.' },
  { who: 'you', text: 'Put the ask before the team slide.' },
  { who: 'agent', text: 'Done. Moved it, and the team is now the last thing they read.' },
];

/** x.ai's card: 8px under the bar. */
const CARD_GAP = 8;

export interface HeaderNavV2Props {
  className?: string;
  signUpHref?: string;
}

export function HeaderNavV2({ className, signUpHref = '/sign-in' }: HeaderNavV2Props) {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  /** The card's box, in the header's coordinates, transitioned between menus. */
  const [box, setBox] = useState<{ left: number; width: number; height: number } | null>(null);
  const lastScrollYRef = useRef(0);
  const rootRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const triggers = useRef(new Map<string, HTMLButtonElement>());
  const closeTimer = useRef(0);

  useEffect(() => {
    const SHOW_AT_TOP_PX = 8;
    const HIDE_AFTER_PX = 80;
    const DIRECTION_DELTA_PX = 4;
    const onScroll = () => {
      const y = window.scrollY;
      const last = lastScrollYRef.current;
      setScrolled(y > 1);
      if (y < SHOW_AT_TOP_PX) setHidden(false);
      else if (y > last + DIRECTION_DELTA_PX && y > HIDE_AFTER_PX) {
        setHidden(true);
        setOpen(null);
      } else if (y < last - DIRECTION_DELTA_PX) setHidden(false);
      lastScrollYRef.current = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  // The card follows the open trigger and takes the content's own size, so the transition has real
  // numbers to run between. Measured after the content commits, before paint.
  useIsomorphicLayoutEffect(() => {
    if (!open) return;
    const trigger = triggers.current.get(open);
    const content = contentRef.current;
    const root = rootRef.current;
    if (!trigger || !content || !root) return;
    const rootBox = root.getBoundingClientRect();
    const triggerBox = trigger.getBoundingClientRect();
    setBox({
      left: triggerBox.left - rootBox.left,
      width: content.offsetWidth,
      height: content.offsetHeight,
    });
  }, [open]);

  const show = useCallback((key: string) => {
    window.clearTimeout(closeTimer.current);
    setOpen(key);
  }, []);
  const scheduleClose = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(null), 120);
  }, []);
  const cancelClose = useCallback(() => window.clearTimeout(closeTimer.current), []);

  const menu = MENUS.find((m) => m.key === open) ?? null;

  return (
    <header
      ref={rootRef}
      className={cn(styles.nav, scrolled && styles.navScrolled, hidden && styles.navHidden, className)}
      data-name="HeaderNavV2"
      onMouseLeave={scheduleClose}
      onMouseEnter={cancelClose}
    >
      <div className={styles.navInner}>
        <div className={styles.navLeft}>
          <a className={styles.logoLink} href="/" aria-label="Deck home" onClick={handleLandingLogoHomeClick}>
            <img className={styles.logoMark} src="/landing/logo/deck-symbol.svg" alt="" width={24} height={22} />
          </a>

          <nav className={styles.navLinks} aria-label="Primary">
            {NAV_LINKS.map((link) =>
              link.menu ? (
                <button
                  key={link.label}
                  ref={(el) => {
                    if (el) triggers.current.set(link.menu!, el);
                    else triggers.current.delete(link.menu!);
                  }}
                  type="button"
                  className={cn(styles.navLink, open === link.menu && styles.navLinkOpen)}
                  aria-expanded={open === link.menu}
                  aria-haspopup="true"
                  onMouseEnter={() => show(link.menu!)}
                  onFocus={() => show(link.menu!)}
                  onClick={() => setOpen((v) => (v === link.menu ? null : link.menu!))}
                >
                  {link.label}
                  <svg className={styles.chevron} width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M6 9.5l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              ) : (
                <a
                  key={link.label}
                  className={styles.navLink}
                  href={link.href}
                  onMouseEnter={scheduleClose}
                  onClick={(e) => handleInPageHashLinkClick(e, link.href)}
                >
                  {link.label}
                </a>
              ),
            )}
          </nav>
        </div>

        <div className={styles.navActions}>
          <a className={styles.signUpLink} href={signUpHref}>
            Sign up
          </a>
        </div>
      </div>
      <div className={styles.navHairline} aria-hidden />

      {menu && box ? (
        <div
          className={styles.card}
          style={{ left: box.left, width: box.width, height: box.height, top: `calc(100% + ${CARD_GAP}px)` } as CSSProperties}
          role="menu"
          aria-label={menu.label}
          onMouseEnter={cancelClose}
        >
          {/* Keyed on the menu, so a change of menu mounts fresh content that fades and drifts in
              while the card itself slides to it. */}
          <div key={menu.key} ref={contentRef} className={cn(styles.content, menu.preview && styles.contentWithPreview)}>
            <div className={styles.list}>
              {menu.items.map((item) => (
                <a
                  key={item.title}
                  className={styles.item}
                  href={item.href}
                  role="menuitem"
                  onClick={(e) => {
                    setOpen(null);
                    handleInPageHashLinkClick(e, item.href);
                  }}
                >
                  <span className={styles.itemTitle}>{item.title}</span>
                  <span className={styles.itemDescription}>{item.description}</span>
                </a>
              ))}
              {menu.more ? (
                <a className={styles.more} href={menu.more.href} role="menuitem" onClick={(e) => { setOpen(null); handleInPageHashLinkClick(e, menu.more!.href); }}>
                  {menu.more.label}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M7 17L17 7M9 7h8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
              ) : null}
            </div>
            {menu.preview ? (
              <div className={styles.preview} aria-hidden>
                <div className={styles.previewWell}>
                  <div className={styles.chat}>
                    {CHAT.map((turn, i) => (
                      <div key={i} className={cn(styles.turn, turn.who === 'you' && styles.turnYou)}>
                        <div className={cn(styles.bubble, turn.who === 'you' ? styles.bubbleYou : styles.bubbleAgent)}>{turn.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : menu ? (
        // First frame of a fresh open: content mounted off-screen so it can be measured.
        <div className={styles.card} style={{ left: 0, top: `calc(100% + ${CARD_GAP}px)`, visibility: 'hidden' }}>
          <div key={menu.key} ref={contentRef} className={cn(styles.content, menu.preview && styles.contentWithPreview)}>
            <div className={styles.list}>
              {menu.items.map((item) => (
                <span key={item.title} className={styles.item}>
                  <span className={styles.itemTitle}>{item.title}</span>
                  <span className={styles.itemDescription}>{item.description}</span>
                </span>
              ))}
              {menu.more ? <span className={styles.more}>{menu.more.label}</span> : null}
            </div>
            {menu.preview ? <div className={styles.preview} /> : null}
          </div>
        </div>
      ) : null}
    </header>
  );
}

HeaderNavV2.displayName = 'HeaderNavV2';
